import { ref, watch } from 'vue';
import {
    useIntersectionObserver,
    useSwipe,
    useIntervalFn,
    useFocusWithin,
} from '@vueuse/core';

import { BACKWARD, FORWARD, SWIPE_DIRECTIONS } from '~coreModules/core/js/constants';

export function useAutoplay(container, slidesContainer, numberOfSlides, autoplayInterval = 5000) {
    const currentSlideIndex = ref(0);
    const isVisible = ref(false);

    const { focused } = useFocusWithin(container);

    useIntersectionObserver(
        slidesContainer,
        ([entry]) => {
            isVisible.value = entry?.isIntersecting || false;
        },
        {
            immediate: true,
            rootMargin: '0px 0px 0px 0px',
            threshold: [0, 0.25, 0.5, 0.75, 1.0],
        },
    );

    useSwipe(slidesContainer, {
        passive: true,
        onSwipeEnd: (event, direction) => {
            // eslint-disable-next-line no-use-before-define
            changePage(direction === SWIPE_DIRECTIONS.LEFT ? FORWARD : BACKWARD, true);
        },
    });

    const { pause, resume, isActive } = useIntervalFn(() => {
        // eslint-disable-next-line no-use-before-define
        changePage(FORWARD);
    }, autoplayInterval);

    function changePage(direction, isManualNavigation = false) {
        if (direction === BACKWARD) {
            currentSlideIndex.value = (currentSlideIndex.value - 1 + numberOfSlides) % numberOfSlides;
        } else {
            currentSlideIndex.value = (currentSlideIndex.value + 1) % numberOfSlides;
        }

        if (isManualNavigation) {
            pause();
        }
    }

    function toggleAutoplay() {
        if (isActive.value) {
            pause();
        } else {
            resume();
        }
    }

    function goToSlideIndex(index) {
        currentSlideIndex.value = index;
        pause();
    }

    watch(isVisible, (newValue, oldValue) => {
        if (newValue) {
            resume();
        } else if (!newValue && oldValue) {
            pause();
        }
    });

    watch(focused, (newValue) => {
        if (newValue) {
            pause();
        }
    });

    return {
        isActive,
        currentSlideIndex,
        toggleAutoplay,
        changePage,
        goToSlideIndex,
    };
}

export default {
    useAutoplay,
};
